import ky from "ky";
import { isEmpty } from "lodash-es";
import { getStore } from "../store";
import { ObjectType } from "./status";
import { ILiftStatusAuth, ILiftStatusSensorResponse } from "./types";

export type TResetCommand = "reset_contact_when_idle" | "reset_contact";

export interface IApiConfig {
  apiUrl: string;
  accessToken: string;
}

const REQUEST_TIMEOUT_MS = 60000;

const authHeader = (accessToken: string) => ({
  Authorization: `Bearer ${accessToken}`,
});

export class MissingApiConfigError extends Error {
  constructor(message: string) {
    super(message);
    this.name = "MissingApiConfigError";
  }
}

export async function authenticate({
  apiUrl,
  accessToken,
}: IApiConfig): Promise<ILiftStatusAuth> {
  return ky
    .post(`${apiUrl}/liftstatus/auth`, {
      headers: authHeader(accessToken),
      json: { accessToken },
      throwHttpErrors: true,
    })
    .json();
}

export async function fetchSensorSummary({
  apiUrl,
  accessToken,
  objectType,
  objectId,
  sensorId,
  from,
}: IApiConfig & {
  objectType: ObjectType;
  objectId: string;
  sensorId: number;
  from: string;
}): Promise<ILiftStatusSensorResponse> {
  return ky
    .get(
      `${apiUrl}/liftstatus/${objectType}/${objectId}/sensor/${sensorId}/summary`,
      {
        headers: authHeader(accessToken),
        searchParams: { from },
        throwHttpErrors: true,
        timeout: REQUEST_TIMEOUT_MS,
      },
    )
    .json();
}

export async function fetchSensorData({
  apiUrl,
  accessToken,
  objectType,
  objectId,
  from,
  to,
  sensors,
}: IApiConfig & {
  objectType: ObjectType;
  objectId: string;
  from: string;
  to: string;
  sensors: number[];
}): Promise<ILiftStatusSensorResponse[]> {
  return ky
    .post(`${apiUrl}/liftstatus/${objectType}/${objectId}/sensor`, {
      headers: authHeader(accessToken),
      searchParams: {
        from,
        to,
        sensors: sensors.map(String).join(),
      },
      throwHttpErrors: true,
      timeout: REQUEST_TIMEOUT_MS,
    })
    .json();
}

export async function sendResetCommand({
  idKey,
  command,
}: {
  idKey: string | undefined;
  command: TResetCommand;
}): Promise<void> {
  const store = await getStore(idKey);
  const { accessToken, apiUrl, externalIdentifier, objectType } = store.state;

  if (
    apiUrl === undefined ||
    accessToken === undefined ||
    externalIdentifier === undefined ||
    objectType === undefined ||
    isEmpty(apiUrl) ||
    isEmpty(accessToken) ||
    isEmpty(externalIdentifier) ||
    isEmpty(objectType)
  ) {
    throw new MissingApiConfigError(
      "Cannot send reset command: missing API URL, access token, object type, or external identifier.",
    );
  }

  await ky.post(
    `${apiUrl}/liftstatus/command/${objectType}/${externalIdentifier}/${command}`,
    {
      headers: authHeader(accessToken),
      throwHttpErrors: true,
    },
  );
}
